import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Send, Copy, Check, X, Sparkles, AlertCircle, History } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GIFT_TYPES = [
  { id: 'coffee_reading', name: 'Kahve Falı', cost: 1, icon: '☕', description: 'AI destekli kahve falı' },
  { id: 'premium_tarot', name: 'Premium Tarot', cost: 3, icon: '🎴', description: 'Derinlemesine tarot açılımı' },
  { id: 'relationship', name: 'İlişki Analizi', cost: 2, icon: '💕', description: 'İlişki uyum analizi' },
  { id: 'natal_chart', name: 'Doğum Haritası', cost: 3, icon: '🌟', description: 'Kişisel doğum haritası' },
  { id: 'oracle', name: 'Oracle Açılımı', cost: 5, icon: '🔮', description: 'Derin sezgisel açılım', premium: true }
];

const GiftFortuneModal = ({ isOpen, onClose, userCredits, onGiftSent, isUnlimited = false }) => {
  const [step, setStep] = useState(1); // 1: select, 2: form, 3: success, 4: history
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientPhone: '',
    senderName: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [giftResult, setGiftResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [giftHistory, setGiftHistory] = useState({ sent: [], received: [] });
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedType(null);
      setFormData({ recipientEmail: '', recipientPhone: '', senderName: '', message: '' });
      setGiftResult(null);
      setError(null);
    }
  }, [isOpen]);

  const handleSelectType = (type) => {
    const canAfford = isUnlimited || type.cost <= userCredits;
    if (!canAfford) {
      setError(`Bu hediye için ${type.cost} kredi gerekiyor.`);
      return;
    }
    setSelectedType(type);
    setError(null);
    setStep(2);
    
    // Track event
    trackEvent('gift_type_selected', null, { gift_type: type.id });
  };

  const trackEvent = async (eventType, giftCode = null, metadata = null) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/gifts/track-event?event_type=${eventType}${giftCode ? `&gift_code=${giftCode}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: metadata ? JSON.stringify(metadata) : undefined
      });
    } catch (e) {
      console.log('Event tracking failed:', e);
    }
  };

  const handleSendGift = async () => {
    if (!formData.senderName.trim()) {
      setError('Lütfen adınızı girin');
      return;
    }

    setLoading(true);
    setError(null);
    
    trackEvent('gift_send_started');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Hediye göndermek için giriş yapmanız gerekiyor');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/gifts/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gift_type: selectedType.id,
          recipient_email: formData.recipientEmail || null,
          recipient_phone: formData.recipientPhone || null,
          sender_name: formData.senderName,
          message: formData.message || null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setGiftResult(data);
        setStep(3);
        if (onGiftSent) onGiftSent(data.credits_used || selectedType.cost);
      } else {
        setError(data.detail || 'Hediye gönderilemedi');
      }
    } catch (error) {
      console.error('Gift send error:', error);
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gifts/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGiftHistory({
          sent: data.history.filter(h => h.type === 'sent'),
          received: data.history.filter(h => h.type === 'received')
        });
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const copyGiftLink = () => {
    if (giftResult?.redeem_url) {
      navigator.clipboard.writeText(giftResult.redeem_url);
      setCopied(true);
      trackEvent('gift_share_clicked', giftResult.gift_code, { method: 'copy' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    if (giftResult?.share_text_whatsapp) {
      trackEvent('gift_share_clicked', giftResult.gift_code, { method: 'whatsapp' });
      window.open(`https://wa.me/?text=${encodeURIComponent(giftResult.share_text_whatsapp)}`, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
            borderRadius: '24px',
            padding: '28px',
            width: '100%',
            maxWidth: '480px',
            maxHeight: '85vh',
            overflowY: 'auto',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Gift size={24} color="white" />
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>Fal Hediye Et</h2>
                <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                  {step === 1 && 'Hediye türünü seç'}
                  {step === 2 && 'Kime göndereceksin?'}
                  {step === 3 && 'Hediye hazır!'}
                  {step === 4 && 'Hediye Geçmişi'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {step === 1 && (
                <button
                  onClick={() => { setStep(4); loadHistory(); }}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '8px',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                  title="Hediye Geçmişi"
                >
                  <History size={20} />
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '8px',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={18} color="#ef4444" />
              <span style={{ color: '#f87171', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Step 1: Select Gift Type */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                  Kredileriniz: <strong style={{ color: isUnlimited ? '#22c55e' : '#c084fc' }}>
                    {isUnlimited ? '∞' : userCredits}
                  </strong>
                </span>
              </div>
              
              {GIFT_TYPES.map((type) => {
                const canAfford = isUnlimited || type.cost <= userCredits;
                return (
                  <motion.button
                    key={type.id}
                    whileHover={canAfford ? { scale: 1.02 } : {}}
                    whileTap={canAfford ? { scale: 0.98 } : {}}
                    onClick={() => handleSelectType(type)}
                    disabled={!canAfford}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: canAfford 
                        ? 'rgba(139, 92, 246, 0.1)' 
                        : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${canAfford ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '16px',
                      cursor: canAfford ? 'pointer' : 'not-allowed',
                      opacity: canAfford ? 1 : 0.5,
                      textAlign: 'left',
                      width: '100%'
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: type.premium 
                        ? 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)'
                        : 'rgba(139, 92, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      {type.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        color: 'white', 
                        fontWeight: '600', 
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {type.name}
                        {type.premium && (
                          <span style={{
                            background: 'linear-gradient(135deg, #f59e0b, #ec4899)',
                            padding: '2px 8px',
                            borderRadius: '20px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>PREMIUM</span>
                        )}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
                        {type.description}
                      </div>
                    </div>
                    <div style={{
                      background: canAfford ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                      color: canAfford ? '#22c55e' : '#ef4444',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {type.cost} kredi
                    </div>
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* Step 2: Recipient Form */}
          {step === 2 && selectedType && (
            <div>
              <div style={{
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '32px' }}>{selectedType.icon}</span>
                <div>
                  <div style={{ color: 'white', fontWeight: '600' }}>{selectedType.name}</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{selectedType.cost} kredi kullanılacak</div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>
                    Senin Adın *
                  </label>
                  <input
                    type="text"
                    value={formData.senderName}
                    onChange={e => setFormData({ ...formData, senderName: e.target.value })}
                    placeholder="Adını yaz"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '15px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', marginBottom: '6px' }}>
                    Kişisel Mesajın (opsiyonel)
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Sana özel bir hediye..."
                    rows={3}
                    maxLength={200}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: 'white',
                      fontSize: '15px',
                      resize: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <div style={{ textAlign: 'right', fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>
                    {formData.message.length}/200
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => { setStep(1); setError(null); }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '15px'
                  }}
                >
                  Geri
                </button>
                <button
                  onClick={handleSendGift}
                  disabled={loading || !formData.senderName.trim()}
                  style={{
                    flex: 2,
                    padding: '14px',
                    background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: loading || !formData.senderName.trim() ? 0.7 : 1
                  }}
                >
                  {loading ? (
                    'Gönderiliyor...'
                  ) : (
                    <>
                      <Send size={18} />
                      Hediyeyi Oluştur
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && giftResult && (
            <div style={{ textAlign: 'center' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}
              >
                <Sparkles size={40} color="white" />
              </motion.div>

              <h3 style={{ color: 'white', margin: '0 0 8px', fontSize: '22px' }}>
                Hediye Hazır! 🎁
              </h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 24px', fontSize: '14px' }}>
                Linki paylaşarak hediyeni gönderebilirsin
              </p>

              {/* Gift Code */}
              <div style={{
                background: 'rgba(139, 92, 246, 0.2)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '4px' }}>
                  Hediye Kodu
                </div>
                <div style={{ 
                  color: 'white', 
                  fontSize: '28px', 
                  fontWeight: 'bold',
                  letterSpacing: '4px',
                  fontFamily: 'monospace'
                }}>
                  {giftResult.gift_code}
                </div>
              </div>

              {/* Copy Link Button */}
              <button
                onClick={copyGiftLink}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: copied 
                    ? 'rgba(34, 197, 94, 0.2)' 
                    : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                  border: copied ? '2px solid #22c55e' : 'none',
                  borderRadius: '12px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                {copied ? (
                  <>
                    <Check size={18} />
                    Link Kopyalandı!
                  </>
                ) : (
                  <>
                    <Copy size={18} />
                    Hediye Linkini Kopyala
                  </>
                )}
              </button>

              {/* Share Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={shareWhatsApp}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(37, 211, 102, 0.2)',
                    border: '1px solid rgba(37, 211, 102, 0.3)',
                    borderRadius: '10px',
                    color: '#25D366',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  WhatsApp
                </button>
                <a
                  href={`mailto:?subject=🎁 Sana bir hediye!&body=${encodeURIComponent(giftResult.share_text_email || '')}`}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}
                >
                  E-posta
                </a>
              </div>

              <button
                onClick={onClose}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginTop: '16px'
                }}
              >
                Kapat
              </button>
            </div>
          )}

          {/* Step 4: History */}
          {step === 4 && (
            <div>
              <button
                onClick={() => setStep(1)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                  marginBottom: '16px'
                }}
              >
                ← Geri
              </button>

              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                  Yükleniyor...
                </div>
              ) : (
                <>
                  {/* Sent Gifts */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ color: 'white', margin: '0 0 12px', fontSize: '14px' }}>
                      📤 Gönderdiğim Hediyeler ({giftHistory.sent.length})
                    </h4>
                    {giftHistory.sent.length === 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>Henüz hediye göndermediniz</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {giftHistory.sent.slice(0, 5).map((g, i) => (
                          <div key={i} style={{
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '10px',
                            padding: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '20px' }}>{g.gift_icon}</span>
                              <div>
                                <div style={{ color: 'white', fontSize: '13px' }}>{g.gift_name}</div>
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                                  {new Date(g.date).toLocaleDateString('tr-TR')}
                                </div>
                              </div>
                            </div>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: g.status === 'redeemed' ? 'rgba(34,197,94,0.2)' : 'rgba(251,191,36,0.2)',
                              color: g.status === 'redeemed' ? '#22c55e' : '#fbbf24'
                            }}>
                              {g.status === 'redeemed' ? 'Kullanıldı' : 'Bekliyor'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Received Gifts */}
                  <div>
                    <h4 style={{ color: 'white', margin: '0 0 12px', fontSize: '14px' }}>
                      📥 Aldığım Hediyeler ({giftHistory.received.length})
                    </h4>
                    {giftHistory.received.length === 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>Henüz hediye almadınız</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {giftHistory.received.slice(0, 5).map((g, i) => (
                          <div key={i} style={{
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '10px',
                            padding: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                          }}>
                            <span style={{ fontSize: '20px' }}>{g.gift_icon}</span>
                            <div>
                              <div style={{ color: 'white', fontSize: '13px' }}>{g.gift_name}</div>
                              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                                {g.other_party} • {new Date(g.date).toLocaleDateString('tr-TR')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GiftFortuneModal;
